import { useEffect, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { Filter, ResolvedFilter } from '../../domain';
import { mergeLockedFilters } from '../../domain/query/locked-filters';
import type { TableAdapter } from '../../ports';

export interface LockedFiltersResult<T> {
    adapter: TableAdapter<T>;
    userFilters: MutableRefObject<ResolvedFilter[]>;
}

export function useLockedFilters<T>(
    adapter: TableAdapter<T>,
    locked: ResolvedFilter[],
): LockedFiltersResult<T> {
    const lockedRef = useRef<ResolvedFilter[]>(locked);
    lockedRef.current = locked;

    const userFilters = useRef<ResolvedFilter[]>([]);

    const wrapped = useMemo<TableAdapter<T>>(() => {
        function apply(requested: ResolvedFilter[]): void {
            userFilters.current = requested;
            adapter.setFilters(
                mergeLockedFilters(requested, lockedRef.current),
            );
        }
        return {
            getSnapshot: adapter.getSnapshot.bind(adapter),
            subscribe: adapter.subscribe.bind(adapter),
            setSort: adapter.setSort.bind(adapter),
            setSearch: adapter.setSearch.bind(adapter),
            refresh: adapter.refresh.bind(adapter),
            canSortBy: adapter.canSortBy?.bind(adapter),
            next: adapter.next?.bind(adapter),
            prev: adapter.prev?.bind(adapter),
            first: adapter.first?.bind(adapter),
            last: adapter.last?.bind(adapter),
            goTo: adapter.goTo?.bind(adapter),
            applyDefaultPageSize: adapter.applyDefaultPageSize?.bind(adapter),
            getPageSize: adapter.getPageSize?.bind(adapter),
            setVisibleFields: adapter.setVisibleFields?.bind(adapter),
            setFilter: (key: string, filter: Filter | null) => {
                const rest = userFilters.current.filter((f) => f.key !== key);
                apply(filter ? [...rest, { ...filter, key }] : rest);
            },
            setFilters: apply,
        };
    }, [adapter]);

    useEffect(() => {
        adapter.setFilters(
            mergeLockedFilters(userFilters.current, lockedRef.current),
        );
    }, [adapter, locked]);

    return { adapter: wrapped, userFilters };
}
